import type { FC, ReactNode } from 'react'

import {
    Factory,
    FlaskConical,
    Package,
    Ship,
    Sprout
} from 'lucide-react'

import { Container } from '@/components/generals/container'
import { SectionHeading } from '@/components/generals/section-heading'

const STEPS = [
    {
        icon: Sprout,
        title: 'Sourcing',
        text: 'We buy paddy and milled rice from established mills in the main growing regions, season after season.'
    },
    {
        icon: Factory,
        title: 'Milling and sorting',
        text: 'Rice is cleaned, polished and sortex sorted to the grain length and broken percentage you need.'
    },
    {
        icon: FlaskConical,
        title: 'Testing and samples',
        text: 'Each lot is sampled and tested, and samples go to you for approval before you commit to volume.'
    },
    {
        icon: Package,
        title: 'Packing',
        text: 'Packed in the bag type and size you choose, plain or printed with your brand.'
    },
    {
        icon: Ship,
        title: 'Dispatch and documents',
        text: 'Delivered by truck or loaded into containers, with invoices, certificates and inspection reports.'
    }
]

export const ProcessSection: FC = (): ReactNode => {
    return (
        <section
            id='process'
            aria-labelledby='process-title'
            className='relative isolate overflow-hidden bg-primary py-20 text-primary-foreground sm:py-24 lg:py-28'
        >
            <svg
                aria-hidden='true'
                focusable='false'
                className='pointer-events-none absolute inset-0 -z-10 size-full text-primary-foreground/[0.06]'
            >
                <defs>
                    <pattern id='process-grain-pattern' width='64' height='64' patternUnits='userSpaceOnUse'>
                        <ellipse cx='16' cy='16' rx='2.8' ry='7.5' transform='rotate(35 16 16)' fill='currentColor' />
                        <ellipse cx='48' cy='46' rx='2.8' ry='7.5' transform='rotate(-25 48 46)' fill='currentColor' />
                    </pattern>
                </defs>

                <rect width='100%' height='100%' fill='url(#process-grain-pattern)' />
            </svg>

            <Container>
                <SectionHeading
                    id='process-title'
                    tone='dark'
                    eyebrow='How we work'
                    title='From paddy to your warehouse'
                    description='Five steps and one trade desk, so you always know where your order stands.'
                />

                <ol className='mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5'>
                    {STEPS.map((step, index) => (
                        <li
                            key={step.title}
                            className='relative rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 p-6'
                        >
                            <div className='flex items-center justify-between'>
                                <span className='flex size-11 items-center justify-center rounded-xl bg-gold text-gold-foreground'>
                                    <step.icon className='size-5' aria-hidden='true' />
                                </span>

                                <span className='font-heading text-3xl text-primary-foreground/30' aria-hidden='true'>
                                    {String(index + 1).padStart(2, '0')}
                                </span>
                            </div>

                            <h3 className='mt-6 text-lg font-bold'>
                                {step.title}
                            </h3>

                            <p className='mt-2 text-sm leading-relaxed text-primary-foreground/75'>
                                {step.text}
                            </p>
                        </li>
                    ))}
                </ol>
            </Container>
        </section>
    )
}
